// 1_utils/delivery_date.js
// Checkout shipping ETA: cutoff, working days, public holidays (Nager.Date),
// and copy like "v úterý 1.9." / personal-pickup hours. Used from 3_pages/ordering_process_2.js.

import { byShop } from "../0_global/shop_map.js";

const HOLIDAY_TTL_MS = 24 * 60 * 60 * 1000;
const HOLIDAY_FETCH_TIMEOUT_MS = 8000;
const BACKUP_FIXED_HOLIDAYS = {
	CZ: ["01-01", "05-01", "05-08", "07-05", "07-06", "09-28", "10-28", "11-17", "12-24", "12-25", "12-26"],
	SK: ["01-01", "01-06", "05-01", "05-08", "07-05", "08-29", "09-01", "09-15", "11-01", "11-17", "12-24", "12-25", "12-26"],
	PL: ["01-01", "01-06", "05-01", "05-03", "08-15", "11-01", "11-11", "12-24", "12-25", "12-26"],
};

function holidayCountryCode() {
	return byShop({ cs: "CZ", sk: "SK", pl: "PL" });
}

function shopTimeZone() {
	return byShop({ cs: "Europe/Prague", sk: "Europe/Prague", pl: "Europe/Warsaw" });
}

function dispatchCutoffHour() {
	return byShop({ cs: 14, sk: 14, pl: 13 });
}

function holidayStorageKeys() {
	const country = holidayCountryCode();
	return {
		data: `natDeliveryHolidays_${country}`,
		expiry: `natDeliveryHolidays_${country}_expiry`,
	};
}

function toDateKey(date) {
	return `${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`;
}

function addCalendarDays(date, days) {
	const utc = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
	return {
		year: utc.getUTCFullYear(),
		month: utc.getUTCMonth() + 1,
		day: utc.getUTCDate(),
	};
}

function weekdayOf(date) {
	return new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay();
}

function getZonedNow(timeZone) {
	const parts = new Intl.DateTimeFormat("en-GB", {
		timeZone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		hourCycle: "h23",
	}).formatToParts(new Date());
	const map = Object.fromEntries(parts.filter((part) => part.type !== "literal").map((part) => [part.type, part.value]));
	return {
		year: Number(map.year),
		month: Number(map.month),
		day: Number(map.day),
		hour: Number(map.hour),
		minute: Number(map.minute),
	};
}

function isAfterHour(now, hour) {
	return now.hour > hour || (now.hour === hour && now.minute > 0);
}

function timeMinutes(now) {
	return now.hour * 60 + now.minute;
}

function isBeforeTime(now, hour, minute) {
	return timeMinutes(now) < hour * 60 + minute;
}

function isAfterTime(now, hour, minute) {
	return timeMinutes(now) > hour * 60 + minute;
}

function isHoliday(date, holidays) {
	return holidays.has(toDateKey(date));
}

function isWorkingDay(date, holidays) {
	const weekday = weekdayOf(date);
	if (weekday === 0 || weekday === 6) return false;
	return !isHoliday(date, holidays);
}

function nextWorkingDay(date, holidays) {
	let cursor = addCalendarDays(date, 1);
	while (!isWorkingDay(cursor, holidays)) {
		cursor = addCalendarDays(cursor, 1);
	}
	return cursor;
}

function isPickupDay(date, holidays) {
	if (weekdayOf(date) === 6) return false;
	if (isHoliday(date, holidays)) return false;
	return true;
}

function nextPickupDay(date, holidays) {
	let cursor = addCalendarDays(date, 1);
	while (!isPickupDay(cursor, holidays)) {
		cursor = addCalendarDays(cursor, 1);
	}
	return cursor;
}

function parseHolidayDates(payload) {
	if (!Array.isArray(payload)) return [];
	return payload.map((item) => item?.date).filter((date) => typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date));
}

function easterSunday(year) {
	const a = year % 19;
	const b = Math.floor(year / 100);
	const c = year % 100;
	const d = Math.floor(b / 4);
	const e = b % 4;
	const f = Math.floor((b + 8) / 25);
	const g = Math.floor((b - f + 1) / 3);
	const h = (19 * a + b - d - g + 15) % 30;
	const i = Math.floor(c / 4);
	const k = c % 4;
	const l = (32 + 2 * e + 2 * i - h - k) % 7;
	const m = Math.floor((a + 11 * h + 22 * l) / 451);
	const month = Math.floor((h + l - 7 * m + 114) / 31);
	const day = ((h + l - 7 * m + 114) % 31) + 1;
	return { year, month, day };
}

function backupHolidaysForYear(country, year) {
	const fixed = BACKUP_FIXED_HOLIDAYS[country] || BACKUP_FIXED_HOLIDAYS.CZ;
	const dates = fixed.map((monthDay) => `${year}-${monthDay}`);
	const easter = easterSunday(year);
	if (country === "PL") {
		dates.push(toDateKey(easter), toDateKey(addCalendarDays(easter, 1)), toDateKey(addCalendarDays(easter, 49)), toDateKey(addCalendarDays(easter, 60)));
	} else {
		dates.push(toDateKey(addCalendarDays(easter, -2)), toDateKey(addCalendarDays(easter, 1)));
	}
	return dates;
}

function backupHolidays(country, years) {
	return [...new Set(years.flatMap((year) => backupHolidaysForYear(country, year)))];
}

function mergeHolidayDates(...lists) {
	return [...new Set(lists.flat().filter((date) => typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)))];
}

async function fetchHolidayYear(year, country) {
	const response = await fetch(`https://date.nager.at/api/v3/PublicHolidays/${year}/${country}`, {
		signal: AbortSignal.timeout(HOLIDAY_FETCH_TIMEOUT_MS),
	});
	if (!response.ok) throw new Error("Network response was not ok");
	const dates = parseHolidayDates(await response.json());
	if (dates.length < 8) throw new Error("Holiday payload looks incomplete");
	return dates;
}

function holidaysFromCache() {
	const { data, expiry } = holidayStorageKeys();
	const now = Date.now();
	const expiryValue = localStorage.getItem(expiry);
	const cached = localStorage.getItem(data);
	if (!expiryValue || !cached || now >= parseInt(expiryValue, 10)) return null;
	try {
		const parsed = JSON.parse(cached);
		if (!Array.isArray(parsed)) return null;
		return new Set(parsed);
	} catch (error) {
		return null;
	}
}

function saveHolidayCache(dates) {
	const { data, expiry } = holidayStorageKeys();
	localStorage.setItem(data, JSON.stringify(dates));
	localStorage.setItem(expiry, (Date.now() + HOLIDAY_TTL_MS).toString());
}

export async function loadPublicHolidays() {
	const cached = holidaysFromCache();
	if (cached && cached.size) return cached;

	const country = holidayCountryCode();
	const now = getZonedNow(shopTimeZone());
	const years = [now.year, now.year + 1];
	const backup = backupHolidays(country, years);

	try {
		const lists = await Promise.all(years.map((year) => fetchHolidayYear(year, country)));
		const dates = mergeHolidayDates(lists.flat(), backup);
		saveHolidayCache(dates);
		return new Set(dates);
	} catch (error) {
		saveHolidayCache(backup);
		return new Set(backup);
	}
}

function formatDeliveryDate(date) {
	const weekdayKey = `cartDayOn${weekdayOf(date)}`;
	return `${window.NAT.translations(weekdayKey)} ${date.day}.${date.month}.`;
}

function resolveDispatchDate(today, afterCutoff, holidays) {
	if (isWorkingDay(today, holidays) && !afterCutoff) return today;
	return nextWorkingDay(today, holidays);
}

function resolveCourierDeliveryDate(today, weekday, afterCutoff, isZasilkovna, holidays) {
	if (isZasilkovna && weekday === 6) {
		return nextWorkingDay(today, holidays);
	}

	const dispatch = resolveDispatchDate(today, afterCutoff, holidays);

	if (isZasilkovna && weekdayOf(dispatch) === 5) {
		const saturday = addCalendarDays(dispatch, 1);
		if (isHoliday(saturday, holidays)) return nextWorkingDay(saturday, holidays);
		return saturday;
	}

	return nextWorkingDay(dispatch, holidays);
}

function pickupOnDate(date) {
	return `${window.NAT.translations("cartPickupOn")} ${formatDeliveryDate(date)}`;
}

function resolvePersonalPickupText(now, today, holidays) {
	const weekday = weekdayOf(today);

	if (weekday === 6 || isHoliday(today, holidays)) {
		return pickupOnDate(nextPickupDay(today, holidays));
	}

	if (weekday === 0) {
		if (!isAfterTime(now, 13, 0)) {
			return window.NAT.translations("cartPickupWithin60Min");
		}
		return pickupOnDate(nextWorkingDay(today, holidays));
	}

	if (isBeforeTime(now, 7, 30)) {
		return window.NAT.translations("cartPickupUntilMorning");
	}
	if (!isAfterTime(now, 15, 0)) {
		return window.NAT.translations("cartPickupWithin60Min");
	}
	if (weekday === 5) {
		return pickupOnDate(nextPickupDay(today, holidays));
	}
	return pickupOnDate(nextWorkingDay(today, holidays));
}

export function getDeliveryEtaText({ isZasilkovna = false, isPersonalPickup = false, holidays = new Set() } = {}) {
	const now = getZonedNow(shopTimeZone());
	const today = { year: now.year, month: now.month, day: now.day };
	const weekday = weekdayOf(today);

	if (isPersonalPickup) {
		return resolvePersonalPickupText(now, today, holidays);
	}

	const deliveryDate = resolveCourierDeliveryDate(today, weekday, isAfterHour(now, dispatchCutoffHour()), isZasilkovna, holidays);
	return formatDeliveryDate(deliveryDate);
}
